import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

import bannerAuth from '@/assets/bannerAuth.jpg';
import logoYonyon from '@/assets/logoYonyon.png';

type Mode = 'login' | 'signup';

export default function Auth() {
  const navigate = useNavigate();
  const { login, signup } = useAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAtLeast17, setIsAtLeast17] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setError(null);
    setSubmitting(true);

    const result =
      mode === 'login'
        ? await login(email, password)
        : await signup(name, email, password, isAtLeast17);

    setSubmitting(false);

    if (!result.ok) {
      setError(result.message ?? 'Une erreur est survenue.');
      return;
    }

    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-100 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:h-[580px]">

        {/*
          =========================
          PANNEAU IMAGE / BRANDING
          ========================== 
        */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-2xl">
          <img
            src={bannerAuth}
            alt="Yonyon, la communauté locale autour de Dole"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />

          {/* Overlay pour garder le texte lisible */}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-foreground-950/10" />

          {/* Logo Yonyon */}
          <Link
            to="/"
            className="absolute top-6 left-1/2 -translate-x-1/2 z-10 cursor-pointer"
          >
            <img
              src={logoYonyon}
              alt="Yonyon"
              className="h-24 w-auto object-contain"
            />
          </Link>

          {/* Signature YonYon */}
          <div
            className="
            absolute
            top-32
            left-1/2
            -translate-x-1/2
            z-10
            w-full
            max-w-[300px]
            flex flex-col
            items-center
            text-center
            pointer-events-none
          "
          >
            <p
              className="
              font-heading
              text-lg
              italic
              font-medium
              leading-snug
              text-white
              drop-shadow-sm
            "
            >
              Une application qui te reconnecte
              <br />
              avec les gens autour de toi
            </p>

            <svg
              viewBox="0 0 160 28"
              className="mt-2 w-24 h-auto text-primary-500"
              aria-hidden="true"
            >
              <path
                d="M5 20 C45 29, 105 22, 155 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <div className="relative w-full flex flex-col justify-end p-10">
            <h1 className="mt-6 font-heading text-4xl font-semibold text-white leading-tight max-w-md">
              Sortir, bouger et s'entraider <br />  à Dole.
            </h1>

            <p className="mt-3 text-base text-white/85 max-w-md">
              Propose une activité, rejoins une sortie ou découvre ce qui se passe autour de toi.
            </p>

            {/* Badges */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                <i className="ri-map-pin-line" />
                Activités locales
              </span>

              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                <i className="ri-group-line" />
                Rencontres locales
              </span>

              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                <i className="ri-compass-3-line" />
                Entraide
              </span>
            </div>
          </div>
        </div>

        {/* =========================
            PANNEAU AUTHENTIFICATION
        ========================== */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <div className="w-full max-w-md">

            {/* Logo mobile */}
            <Link
              to="/"
              className="lg:hidden flex items-center justify-center mb-8 cursor-pointer"
            >
              <img
                src={logoYonyon}
                alt="Yonyon"
                className="h-24 w-auto object-cover"
              />
            </Link>

            <div className="bg-white rounded-2xl shadow-sm border border-background-100/50 p-6">

              {/* =========================
                  ONGLETS
              ========================== */}
              <div className="flex rounded-full bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${mode === 'login'
                    ? 'bg-white text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                >
                  Se connecter
                </button>

                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${mode === 'signup'
                    ? 'bg-white text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                >
                  Créer un compte
                </button>
              </div>

              {/* =========================
                  TITRE
              ========================== */}
              <h2 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
                {mode === 'login'
                  ? 'Content de te revoir'
                  : 'Bienvenue parmi nous'}
              </h2>

              <p className="mt-1 text-sm text-foreground-500">
                {mode === 'login'
                  ? 'Connecte-toi pour rejoindre des activités.'
                  : 'Crée ton compte en quelques secondes.'}
              </p>

              {/* =========================
                  FORMULAIRE
              ========================== */}
              <form
                onSubmit={handleSubmit}
                className="mt-4 flex flex-col gap-3"
              >

                {/* Nom */}
                {mode === 'signup' && (
                  <div>
                    <label
                      htmlFor="auth-name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Nom complet
                    </label>

                    <div className="relative">
                      <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                      <input
                        id="auth-name"
                        name="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex : Camille Martin"
                        autoComplete="name"
                        className="w-full pl-10 pr-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label
                    htmlFor="auth-email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Email
                  </label>

                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                    <input
                      id="auth-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="toi@exemple.fr"
                      autoComplete="email"
                      className="w-full pl-10 pr-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                {/* Mot de passe */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      htmlFor="auth-password"
                      className="block text-sm font-medium text-foreground-700"
                    >
                      Mot de passe
                    </label>

                    {mode === 'login' && (
                      <Link
                        to="/mot-de-passe-oublie"
                        className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        Mot de passe oublié ?
                      </Link>
                    )}
                  </div>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                    <input
                      id="auth-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete={
                        mode === 'login'
                          ? 'current-password'
                          : 'new-password'
                      }
                      className="w-full pl-10 pr-10 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword((current) => !current)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                      aria-label={
                        showPassword
                          ? 'Masquer le mot de passe'
                          : 'Afficher le mot de passe'
                      }
                    >
                      <i
                        className={
                          showPassword
                            ? 'ri-eye-off-line'
                            : 'ri-eye-line'
                        }
                      />
                    </button>
                  </div>

                </div>

                {/* =========================
                    CONFIRMATION 18+
                ========================== */}
                {mode === 'signup' && (
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isAtLeast17}
                      onChange={(e) =>
                        setIsAtLeast17(e.target.checked)
                      }
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-background-300 text-primary-500 accent-primary-500 focus:ring-primary-300 cursor-pointer"
                    />

                    <span className="text-sm text-foreground-700 leading-snug">
                      Je déclare être majeur(e) et accepter les règles de YonYon.
                    </span>
                  </label>
                )}

                {/* =========================
                    ERREUR
                ========================== */}
                {error && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* =========================
                    BOUTON
                ========================== */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting && (
                    <i className="ri-loader-4-line animate-spin" />
                  )}

                  {mode === 'login'
                    ? 'Se connecter'
                    : 'Créer mon compte'}
                </button>
              </form>

              {/* =========================
                  RÈGLES
              ========================== */}
              <p className="mt-4 text-xs text-foreground-400 text-center leading-relaxed">
                En continuant, tu acceptes nos règles de communauté.
              </p>
            </div>

            {/* Retour */}
            <Link
              to="/"
              className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}